<script setup>
import { usePrinterStore } from '@/stores/printer'
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'blank',
    action: 'read',
    subject: 'Settings',
  },
})

const route = useRoute()
const router = useRouter()
const { formatRupiah, formatDate } = useFormat()
const printerStore = usePrinterStore()
const { request: sendEmailRequest, loading: sendingEmail, lastError: emailError } = useApiRequest()

const orderId = route.params.id

const order = ref(null)
const tenant = ref(null)
const loading = ref(true)
const loadError = ref(null)

const snackbar = ref({ show: false, text: '', color: 'success' })
const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

// ── Load data order + info toko ────────────────────────────────────────────
const loadData = async () => {
  loading.value = true
  loadError.value = null

  try {
    const [orderRes, settingsRes] = await Promise.all([
      useApi(`/api/v1/orders/${orderId}`).get(),
      useApi('/api/v1/settings').get(),
    ])

    order.value = orderRes?.data ?? orderRes
    tenant.value = (settingsRes?.data ?? settingsRes)?.tenant ?? null
  } catch (e) {
    loadError.value = e?.data?.message ?? 'Struk tidak ditemukan atau gagal dimuat.'
  } finally {
    loading.value = false
  }
}

onMounted(loadData)

// ── Cetak (browser print / thermal) ────────────────────────────────────────
const printBrowser = () => {
  window.print()
}

const printingThermal = ref(false)
const hasPairedPrinter = computed(() => printerStore.printers.length > 0)

const printThermal = async () => {
  printingThermal.value = true
  try {
    await printerStore.enqueueReceipt(orderId)
    showSnackbar('Struk dikirim ke antrean printer thermal.', 'success')
  } catch (e) {
    showSnackbar(e?.message ?? 'Gagal mengirim ke printer thermal.', 'error')
  } finally {
    printingThermal.value = false
  }
}

// ── Kirim WhatsApp (wa.me, input nomor saat tombol diklik) ─────────────────
const waDialog = ref(false)
const waPhone = ref('')

const openWaDialog = () => {
  waPhone.value = ''
  waDialog.value = true
}

// Normalisasi nomor Indonesia: 08xxx / 8xxx / +62xxx -> 62xxx (format wa.me)
const normalizePhone = raw => {
  let digits = (raw || '').replace(/\D/g, '')
  if (digits.startsWith('0')) digits = '62' + digits.slice(1)
  if (!digits.startsWith('62')) digits = '62' + digits
  return digits
}

const buildReceiptText = () => {
  if (!order.value) return ''
  const lines = [
    `*${tenant.value?.name ?? 'Struk Transaksi'}*`,
    `No. Order: ${order.value.order_number}`,
    `Tanggal: ${formatDate(order.value.created_at, { hour: '2-digit', minute: '2-digit' })}`,
    '',
    ...( (order.value.items ?? []).map(i => `${i.quantity}x ${i.product_name_snapshot} - ${formatRupiah(i.subtotal)}`) ),
    '',
    ...( (order.value.discount_breakdown ?? []).map(line => `${line.label}: -${formatRupiah(line.amount)}`) ),
    `Total: ${formatRupiah(order.value.total)}`,
    '',
    'Terima kasih atas kunjungan Anda!',
  ]
  return lines.join('\n')
}

const sendWhatsapp = () => {
  if (!waPhone.value) return
  const phone = normalizePhone(waPhone.value)
  const text = encodeURIComponent(buildReceiptText())
  window.open(`https://wa.me/${phone}?text=${text}`, '_blank')
  waDialog.value = false
}

// ── Kirim Email (backend, SMTP milik tenant) ────────────────────────────────
const emailDialog = ref(false)
const emailAddress = ref('')

const openEmailDialog = () => {
  emailAddress.value = ''
  emailDialog.value = true
}

const sendEmail = async () => {
  if (!emailAddress.value) return

  const res = await sendEmailRequest(() =>
    useApi(`/api/v1/orders/${orderId}/send-email`).post({
      body: { email: emailAddress.value },
    })
  )

  if (res) {
    showSnackbar(`Struk berhasil dikirim ke ${emailAddress.value}`, 'success')
    emailDialog.value = false
  } else if (emailError.value) {
    showSnackbar(emailError.value.message, emailError.value.type ?? 'error')
  }
}

// ── Kembali ke POS ───────────────────────────────────────────────────────────
const backToPos = () => {
  router.push('/pos')
}
</script>

<template>
  <div class="receipt-page">
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 60vh;">
      <VProgressCircular indeterminate color="primary" />
    </div>

    <VAlert v-else-if="loadError" type="error" variant="tonal" class="ma-4">
      {{ loadError }}
    </VAlert>

    <template v-else>
      <VCard max-width="380" class="mx-auto my-4">
        <div id="receipt-print-area" class="receipt pa-4">
          <div class="text-center mb-3">
            <VImg
              v-if="tenant?.receipt_logo"
              :src="tenant.receipt_logo"
              max-width="80"
              max-height="80"
              class="mx-auto mb-2"
              contain
            />
            <div class="text-h6 font-weight-bold">{{ tenant?.name ?? '-' }}</div>
            <div v-if="tenant?.address" class="text-caption">{{ tenant.address }}</div>
            <div v-if="tenant?.phone" class="text-caption">Telp: {{ tenant.phone }}</div>
          </div>

          <VDivider class="mb-2 receipt-divider" />

          <!-- Addendum 16, Modul BV: queue_number null = tidak ikut antrian -->
          <div v-if="order?.queue_number" class="text-center mb-3">
            <div class="text-caption text-medium-emphasis">Nomor Antrian</div>
            <div class="queue-number">{{ order.queue_number }}</div>
          </div>

          <div class="text-caption d-flex justify-space-between">
            <span>No. Order</span>
            <span>{{ order?.order_number }}</span>
          </div>
          <div class="text-caption d-flex justify-space-between">
            <span>Tanggal</span>
            <span>{{ formatDate(order?.created_at, { hour: '2-digit', minute: '2-digit' }) }}</span>
          </div>
          <div v-if="order?.customer_name" class="text-caption d-flex justify-space-between">
            <span>Pelanggan</span>
            <span>{{ order.customer_name }}</span>
          </div>

          <VDivider class="my-2 receipt-divider" />

          <div v-for="item in order?.items ?? []" :key="item.id" class="mb-2">
            <div class="text-body-2 font-weight-medium">
              {{ item.product_name_snapshot }}
              <span v-if="item.variant_name_snapshot && item.variant_name_snapshot.toLowerCase() !== 'default'">
                ({{ item.variant_name_snapshot }})
              </span>
            </div>
            <div class="text-caption d-flex justify-space-between text-medium-emphasis">
              <span>{{ item.quantity }} x {{ formatRupiah(item.unit_price) }}</span>
              <span>{{ formatRupiah(item.subtotal) }}</span>
            </div>
            <div v-if="item.notes" class="text-caption text-medium-emphasis">
              Catatan: {{ item.notes }}
            </div>
          </div>

          <VDivider class="my-2 receipt-divider" />

          <div class="text-body-2 d-flex justify-space-between">
            <span>Subtotal</span>
            <span>{{ formatRupiah(order?.subtotal ?? 0) }}</span>
          </div>
          <div
            v-for="line in order?.discount_breakdown ?? []"
            :key="line.label"
            class="text-body-2 d-flex justify-space-between"
          >
            <span>{{ line.label }}</span>
            <span>-{{ formatRupiah(line.amount) }}</span>
          </div>
          <div v-if="order?.tax_amount > 0" class="text-body-2 d-flex justify-space-between">
            <span>Pajak</span>
            <span>{{ formatRupiah(order.tax_amount) }}</span>
          </div>
          <div class="text-body-1 font-weight-bold d-flex justify-space-between mt-1">
            <span>Total</span>
            <span>{{ formatRupiah(order?.total ?? 0) }}</span>
          </div>

          <VDivider class="my-2 receipt-divider" />

          <div v-for="payment in order?.payments ?? []" :key="payment.id" class="text-caption d-flex justify-space-between">
            <span class="text-capitalize">{{ payment.payment_method }}</span>
            <span>{{ formatRupiah(payment.amount) }}</span>
          </div>
          <div v-if="order?.total_paid > order?.total" class="text-body-2 font-weight-medium d-flex justify-space-between mt-1">
            <span>Kembalian</span>
            <span>{{ formatRupiah(order.total_paid - order.total) }}</span>
          </div>

          <div class="text-center text-caption mt-4">
            Terima kasih atas kunjungan Anda!
          </div>
        </div>

        <VDivider />

        <VCardActions class="d-print-none flex-column ga-2 pa-4">
          <VBtn color="primary" block prepend-icon="ri-printer-line" @click="printBrowser">
            Cetak (Browser)
          </VBtn>
          <VBtn
            v-if="hasPairedPrinter"
            variant="tonal"
            color="primary"
            block
            :loading="printingThermal"
            prepend-icon="ri-printer-cloud-line"
            @click="printThermal"
          >
            Cetak ke Printer Thermal
          </VBtn>
          <VBtn variant="tonal" color="success" block prepend-icon="ri-mail-line" @click="openEmailDialog">
            Kirim Email
          </VBtn>
          <VBtn variant="tonal" color="success" block prepend-icon="ri-whatsapp-line" @click="openWaDialog">
            Kirim WhatsApp
          </VBtn>
          <VBtn variant="text" block prepend-icon="ri-arrow-left-line" @click="backToPos">
            Kembali ke POS
          </VBtn>
        </VCardActions>
      </VCard>
    </template>

    <!-- Dialog input nomor WhatsApp -->
    <VDialog v-model="waDialog" max-width="360" class="d-print-none">
      <VCard>
        <VCardTitle>Kirim Struk via WhatsApp</VCardTitle>
        <VCardText>
          <VTextField
            v-model="waPhone"
            label="Nomor HP Pelanggan"
            placeholder="08xxxxxxxxxx"
            prepend-inner-icon="ri-phone-line"
            autofocus
            @keyup.enter="sendWhatsapp"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="waDialog = false">Batal</VBtn>
          <VBtn color="success" :disabled="!waPhone" @click="sendWhatsapp">Kirim</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Dialog input email -->
    <VDialog v-model="emailDialog" max-width="360" class="d-print-none">
      <VCard>
        <VCardTitle>Kirim Struk via Email</VCardTitle>
        <VCardText>
          <VTextField
            v-model="emailAddress"
            type="email"
            label="Email Pelanggan"
            placeholder="pelanggan@email.com"
            prepend-inner-icon="ri-mail-line"
            autofocus
            @keyup.enter="sendEmail"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="emailDialog = false">Batal</VBtn>
          <VBtn color="success" :loading="sendingEmail" :disabled="!emailAddress" @click="sendEmail">Kirim</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" location="top" class="d-print-none">
      {{ snackbar.text }}
      <template #actions>
        <VBtn variant="text" @click="snackbar.show = false">Tutup</VBtn>
      </template>
    </VSnackbar>
  </div>
</template>

<style scoped>
.receipt {
  font-family: 'Courier New', monospace;
}
.receipt-divider {
  border-style: dashed;
}
.queue-number {
  font-size: 2.5rem;
  font-weight: bold;
  line-height: 1.1;
}

@media print {
  .d-print-none {
    display: none !important;
  }
  .receipt-page {
    background: #fff;
  }
  .receipt {
    width: 100%;
  }
}
</style>
